import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, GripVertical, LayoutDashboard, RotateCcw, Save } from 'lucide-react';

const handles = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
const handleClasses = {
  n: '-top-1 left-1/2 h-2 w-8 -translate-x-1/2 cursor-n-resize',
  ne: '-right-1 -top-1 h-3 w-3 cursor-ne-resize',
  e: '-right-1 top-1/2 h-8 w-2 -translate-y-1/2 cursor-e-resize',
  se: '-bottom-1 -right-1 h-3 w-3 cursor-se-resize',
  s: '-bottom-1 left-1/2 h-2 w-8 -translate-x-1/2 cursor-s-resize',
  sw: '-bottom-1 -left-1 h-3 w-3 cursor-sw-resize',
  w: '-left-1 top-1/2 h-8 w-2 -translate-y-1/2 cursor-w-resize',
  nw: '-left-1 -top-1 h-3 w-3 cursor-nw-resize',
};

function move(items, sourceId, targetId) {
  const from = items.findIndex((item) => item.id === sourceId);
  const to = items.findIndex((item) => item.id === targetId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function ResizeHandles({ panel, onChange }) {
  const beginResize = (event, direction) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = panel.w;
    const startH = panel.h;
    const horizontalSign = direction.includes('w') ? -1 : 1;
    const verticalSign = direction.includes('n') ? -1 : 1;
    const resize = (moveEvent) => {
      const patch = {};
      if (direction.includes('e') || direction.includes('w')) {
        patch.w = Math.max(3, Math.min(12, startW + Math.round(((moveEvent.clientX - startX) * horizontalSign) / 70)));
      }
      if (direction.includes('n') || direction.includes('s')) {
        patch.h = Math.max(1, Math.min(16, startH + Math.round(((moveEvent.clientY - startY) * verticalSign) / 34)));
      }
      onChange(patch);
    };
    const finish = () => {
      window.removeEventListener('pointermove', resize);
      window.removeEventListener('pointerup', finish);
    };
    window.addEventListener('pointermove', resize);
    window.addEventListener('pointerup', finish);
  };

  return handles.map((direction) => (
    <span
      key={direction}
      role="separator"
      aria-label={`Resize ${panel.title} ${direction}`}
      onPointerDown={(event) => beginResize(event, direction)}
      className={`absolute z-20 rounded-full bg-[#D4AF37] ring-2 ring-white ${handleClasses[direction]}`}
    />
  ));
}

export default function AdminAgenciesLayoutEditor({
  open,
  onOpenChange,
  layout,
  setLayout,
  onSave,
  onReset,
  saving,
}) {
  const [tab, setTab] = useState('panels');
  if (!layout) return null;

  const updatePanel = (id, patch) => setLayout((current) => ({
    ...current,
    panels: current.panels.map((panel) => (panel.id === id ? { ...panel, ...patch } : panel)),
  }));
  const updateColumn = (id, patch) => setLayout((current) => ({
    ...current,
    columns: current.columns.map((column) => (column.id === id ? { ...column, ...patch } : column)),
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[94vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
            <LayoutDashboard className="h-5 w-5 text-[#D4AF37]" />
            Edit Agencies Workspace
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 border-b border-[#062B1A]/10">
          {[
            ['panels', 'Panels & sizing'],
            ['columns', 'Table columns'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`border-b-2 px-3 py-2 text-sm font-semibold ${tab === value ? 'border-[#D4AF37] text-[#062B1A]' : 'border-transparent text-[#062B1A]/50'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'panels' ? (
          <div className="space-y-4">
            <p className="text-sm text-[#062B1A]/55">
              Drag panels to reorder. Resize from any edge or corner; panels snap to the responsive 12-column grid.
            </p>
            <div className="grid grid-cols-12 gap-3 rounded-xl border border-[#062B1A]/10 bg-[#F7F8FA] p-4">
              {layout.panels.map((panel) => (
                <div
                  key={panel.id}
                  draggable
                  onDragStart={(event) => event.dataTransfer.setData('text/plain', panel.id)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const sourceId = event.dataTransfer.getData('text/plain');
                    setLayout((current) => ({ ...current, panels: move(current.panels, sourceId, panel.id) }));
                  }}
                  className={`relative min-h-20 rounded-xl border-2 p-3 shadow-sm ${panel.visible ? 'border-[#D4AF37]/45 bg-white' : 'border-dashed border-[#062B1A]/15 bg-white/45 opacity-60'}`}
                  style={{ gridColumn: `span ${panel.w}`, minHeight: `${Math.max(80, panel.h * 34)}px` }}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="mt-2 h-4 w-4 shrink-0 cursor-grab text-[#062B1A]/35" />
                    <div className="min-w-0 flex-1">
                      <Input
                        value={panel.title}
                        onChange={(event) => updatePanel(panel.id, { title: event.target.value })}
                        className="h-8 border-[#062B1A]/10 text-sm font-semibold"
                      />
                      <p className="mt-2 text-xs text-[#062B1A]/45">{panel.w}/12 wide · {panel.h} height units</p>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => updatePanel(panel.id, { visible: !panel.visible })} className="h-8 w-8">
                      {panel.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                  </div>
                  <ResizeHandles panel={panel} onChange={(patch) => updatePanel(panel.id, patch)} />
                </div>
              ))}
            </div>
            <p className="text-xs text-[#062B1A]/45">On phones, visible panels automatically stack to full width.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col justify-between gap-3 rounded-xl border border-[#062B1A]/10 bg-[#F7F8FA] p-3 sm:flex-row sm:items-center">
              <div>
                <p className="text-sm font-semibold text-[#062B1A]">Table row density</p>
                <p className="text-xs text-[#062B1A]/50">Controls the vertical height of every agency row.</p>
              </div>
              <select
                value={layout.row_density}
                onChange={(event) => setLayout((current) => ({ ...current, row_density: event.target.value }))}
                className="h-10 rounded-md border border-[#062B1A]/15 bg-white px-3 text-sm"
              >
                <option value="compact">Compact</option>
                <option value="comfortable">Comfortable</option>
                <option value="spacious">Spacious</option>
              </select>
            </div>
            <p className="text-sm text-[#062B1A]/55">Drag columns to reorder. Rename, hide, or set an exact width.</p>
            <div className="space-y-2">
              {layout.columns.map((column) => (
                <div
                  key={column.id}
                  draggable
                  onDragStart={(event) => event.dataTransfer.setData('text/plain', column.id)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const sourceId = event.dataTransfer.getData('text/plain');
                    setLayout((current) => ({ ...current, columns: move(current.columns, sourceId, column.id) }));
                  }}
                  className="grid grid-cols-[auto_minmax(0,1fr)_110px_auto] items-center gap-2 rounded-lg border border-[#062B1A]/10 p-2"
                >
                  <GripVertical className="h-4 w-4 cursor-grab text-[#062B1A]/35" />
                  <Input value={column.label} onChange={(event) => updateColumn(column.id, { label: event.target.value })} className="h-9" />
                  <div className="relative">
                    <Input
                      type="number"
                      min="60"
                      max="600"
                      value={column.width}
                      onChange={(event) => updateColumn(column.id, { width: Math.max(60, Math.min(600, Number(event.target.value) || 60)) })}
                      className="h-9 pr-8"
                    />
                    <span className="pointer-events-none absolute right-2 top-2.5 text-xs text-[#062B1A]/40">px</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={column.visible && layout.columns.filter((item) => item.visible).length === 1}
                    onClick={() => updateColumn(column.id, { visible: !column.visible })}
                    className="h-9 w-9"
                  >
                    {column.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-[#062B1A]/35" />}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onReset} disabled={saving}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset default
          </Button>
          <Button type="button" onClick={onSave} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save layout'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
